import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Package, 
  Clock, 
  Box, 
  ArrowRight, 
  CheckCircle,
  Users,
  MapPin,
  Building,
  SquareStack,
  ChevronRight,
  Plane
} from "lucide-react";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { t, language } = useLanguage();

  // Hero slider images
  const heroImages = [
    'https://customer-assets.emergentagent.com/job_09913581-f85f-4d5b-af7c-7207d8b9779f/artifacts/7ij43kz4_HG_AB.jpg',
    'https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/o3iuqg2u_HG_2.png'
  ];

  // Auto-slide effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 6000); // Change slide every 6 seconds
    return () => clearInterval(interval);
  }, [heroImages.length]);

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const response = await axios.get(`${API}/blog`);
        setBlogPosts(response.data.slice(0, 3));
      } catch (e) {
        console.error("Error fetching blog posts:", e);
      }
    };
    
    axios.post(`${API}/seed-blog`).catch(() => {});
    fetchBlogPosts();
  }, []);

  const services = [
    {
      icon: Package,
      title: t('services.shipping.title'),
      description: t('services.shipping.description'),
      color: "text-emerald-600",
      bgColor: "bg-emerald-50"
    },
    {
      icon: Plane,
      title: t('services.express.title'),
      description: t('services.express.description'),
      color: "text-emerald-600",
      bgColor: "bg-emerald-50"
    },
    {
      icon: Box,
      title: t('services.fulfillment.title'),
      description: t('services.fulfillment.description'),
      color: "text-emerald-600",
      bgColor: "bg-emerald-50"
    },
    {
      icon: Clock,
      title: t('services.packaging.title'),
      description: t('services.packaging.description'),
      color: "text-emerald-600",
      bgColor: "bg-emerald-50"
    },
  ];

  const expertise = language === 'de' ? [
    {
      title: "Individuelle Betreuung",
      description: "Wir passen uns an Ihre Bedürfnisse an und entwickeln Lösungen, die perfekt zu Ihrem Unternehmen passen.",
    },
    {
      title: "Langjährige Erfahrung",
      description: "Mit unserer Erfahrung in der Logistikbranche können wir Sie umfangreich beraten und unterstützen.",
    },
    {
      title: "Zuverlässigkeit",
      description: "Pünktlichkeit und Zuverlässigkeit sind die Grundpfeiler unserer Arbeit.",
    },
    {
      title: "Modernste Technik",
      description: "Wir arbeiten mit modernen Systemen und können verschiedene Shop-Systeme problemlos anbinden.",
    },
  ] : [
    {
      title: "Individual Support",
      description: "We adapt to your needs and develop solutions that perfectly fit your company.",
    },
    {
      title: "Years of Experience",
      description: "With our experience in the logistics industry, we can provide comprehensive advice and support.",
    },
    {
      title: "Reliability",
      description: "Punctuality and reliability are the cornerstones of our work.",
    },
    {
      title: "State-of-the-Art Technology",
      description: "We work with modern systems and can seamlessly integrate various shop systems.",
    },
  ];

  const stats = [
    { number: "500+", label: t('stats.customers'), icon: Users },
    { number: "20+", label: t('stats.experience'), icon: Building },
    { number: "500", label: t('stats.warehouse'), icon: MapPin },
    { number: "20k+", label: t('stats.packages'), icon: SquareStack },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section - Full Width Image Slider */}
      <section className="relative h-[85vh] min-h-[650px] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Background Image Slider */}
        {heroImages.map((image, index) => (
          <div 
            key={index}
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `url('${image}')`,
            }}
          >
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/50 to-slate-900/80"></div>
          </div>
        ))}
        
        {/* Content */}
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          {/* Target Audience Badge */}
          <div className="inline-flex items-center gap-2 bg-orange-600 text-white text-sm font-bold px-4 py-2 rounded-full mb-6 animate-fade-in-up">
            <span>{language === 'de' ? 'Für Online-Händler, Startups & KMU' : 'For Online Retailers, Startups & SMEs'}</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 uppercase tracking-wide animate-fade-in-up">
            {t('hero.title1')}<br />
            <span className="text-orange-500">{t('hero.title2')}</span>
          </h1>
          
          {/* USP Badges */}
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-8 animate-fade-in-up animation-delay-200">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-lg">
              <span className="text-orange-400 font-bold">{language === 'de' ? 'Same-Day bis 12 Uhr' : 'Same-Day until 12 PM'}</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-lg">
              <span className="text-white font-bold">{language === 'de' ? '180 Länder weltweit' : '180 countries worldwide'}</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-lg">
              <span className="text-white font-bold">{language === 'de' ? 'Ab 20 Sendungen/Monat' : 'From 20 shipments/month'}</span>
            </div>
          </div>
          
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto animate-fade-in-up animation-delay-200">
            {t('hero.subtitleSmall')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animation-delay-300">
            <Link to="/angebot">
              <Button 
                className="bg-orange-600 text-white hover:bg-orange-700 uppercase tracking-wider font-bold px-10 py-6 text-lg transition-all"
                data-testid="hero-cta-button"
              >
                {t('hero.cta')}
              </Button>
            </Link>
            <Link to="/kontakt">
              <Button 
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-slate-900 uppercase tracking-wider font-bold px-10 py-6 text-lg transition-all"
              >
                {language === 'de' ? 'Kostenlos beraten lassen' : 'Free Consultation'}
              </Button>
            </Link>
          </div>
        </div>

        {/* Slider Indicators */}
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-3 z-20">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-orange-500 w-8' 
                  : 'bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`Slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/70 rounded-full animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Services Section - Icon Row */}
      <section className="py-20 bg-white" data-testid="services-section">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-slate-900 uppercase tracking-wide">
            {t('services.title')}
          </h2>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Link
                  to="/leistungen"
                  key={index}
                  className="group text-center"
                  data-testid={`service-card-${index}`}
                >
                  <div className={`w-24 h-24 md:w-32 md:h-32 mx-auto mb-6 ${service.bgColor} rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className={`${service.color} w-12 h-12 md:w-16 md:h-16`} strokeWidth={1.5} aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-lg md:text-xl mb-3 text-slate-900 uppercase tracking-wide">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm md:text-base leading-relaxed hidden md:block">
                    {service.description}
                  </p>
                  <span className="inline-flex items-center text-orange-600 font-semibold mt-4 gap-1 group-hover:gap-2 transition-all text-sm">
                    {t('services.learnMore')}
                    <ChevronRight size={16} aria-hidden="true" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-slate-50" data-testid="about-section">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                {language === 'de' ? (
                  <>Die <strong className="text-slate-900">Kauz Logistik e.K.</strong> bietet Ihnen ein 
                  <strong className="text-slate-900"> starkes Logistiknetzwerk</strong> mit 
                  modernen Lagermöglichkeiten und <strong className="text-slate-900">modernster Technik</strong>.</>
                ) : (
                  <><strong className="text-slate-900">Kauz Logistik e.K.</strong> offers you a 
                  <strong className="text-slate-900"> strong logistics network</strong> with 
                  modern storage facilities and <strong className="text-slate-900">state-of-the-art technology</strong>.</>
                )}
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                {language === 'de' ? (
                  <>Mit unserem Unternehmen können wir Ihnen eine <strong className="text-slate-900">breite Palette 
                  an Logistikdienstleistungen</strong> anbieten, vom Fulfillment über Same Day/Next Day 
                  Lieferungen mit eigenem Fuhrpark bis hin zum Transportmanagement.</>
                ) : (
                  <>With our company, we can offer you a <strong className="text-slate-900">wide range 
                  of logistics services</strong>, from fulfillment to same day/next day 
                  deliveries with our own fleet to transport management.</>
                )}
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                {language === 'de' ? (
                  <>Wir stellen uns <strong className="text-slate-900">individuell auf Ihre Bedürfnisse</strong> ein 
                  und entwickeln gemeinsam mit Ihnen Lösungen, die Ihr Unternehmen voranbringen.</>
                ) : (
                  <>We <strong className="text-slate-900">adapt individually to your needs</strong> and 
                  develop solutions together with you that will advance your business.</>
                )}
              </p>
              <Link to="/unternehmen">
                <Button 
                  variant="outline"
                  className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white uppercase tracking-wider font-bold px-8"
                >
                  {t('about.learnMore')}
                  <ArrowRight className="ml-2" size={16} aria-hidden="true" />
                </Button>
              </Link>
            </div>
            
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_3f8853ef-18d6-476f-b97c-eda985cb2340/artifacts/1giqija1_Lager_1.jpg"
                alt={language === 'de' ? "Lagerhaus mit Regalen" : "Warehouse with shelves"}
                className="w-full shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-white" data-testid="expertise-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 uppercase tracking-wide">
              {language === 'de' ? 'Profitieren Sie von unserer Expertise' : 'Benefit from Our Expertise'}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {expertise.map((item, index) => (
              <div 
                key={item.title} 
                className="text-center p-6"
                data-testid={`expertise-item-${index}`}
              >
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="text-orange-600 w-8 h-8" aria-hidden="true" />
                </div>
                <h4 className="font-bold text-lg mb-3 text-slate-900">{item.title}</h4>
                <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-slate-900 text-white" data-testid="stats-section">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 uppercase tracking-wide">
            {t('stats.title')}
          </h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-4">
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <div 
                  key={stat.label} 
                  className="text-center lg:border-r last:border-r-0 border-slate-700 px-4"
                  data-testid={`stat-item-${index}`}
                >
                  <div className="mono text-4xl md:text-5xl lg:text-6xl font-bold text-orange-500 mb-2">{stat.number}</div>
                  <div className="text-slate-400 uppercase tracking-wider text-sm">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-24 relative bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/4483775/pexels-photo-4483775.jpeg?auto=compress&cs=tinysrgb&w=1920')`,
        }}
        data-testid="cta-section"
      >
        <div className="absolute inset-0 bg-slate-900/80"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 text-white">
            {t('cta.title')}
          </h2>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
            {t('cta.description')}
          </p>
          <Link to="/angebot">
            <Button 
              className="bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-wider font-bold px-12 py-6 text-lg"
              data-testid="cta-button"
            >
              {t('cta.button')}
            </Button>
          </Link>
        </div>
      </section>

      {/* Blog Preview Section */}
      {blogPosts.length > 0 && (
        <section className="py-20 bg-white" data-testid="blog-preview-section">
          <div className="container mx-auto px-4">
            <div className="flex justify-between items-end mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 uppercase tracking-wide">
                {t('blog.title')}
              </h2>
              <Link 
                to="/blog" 
                className="hidden md:flex items-center gap-2 text-orange-600 font-semibold hover:gap-3 transition-all"
              >
                {t('blog.viewAll')}
                <ArrowRight size={16} aria-hidden="true" />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => (
                <Link 
                  key={post.id} 
                  to={`/blog/${post.slug}`}
                  className="blog-card bg-white border border-slate-200 overflow-hidden group"
                  data-testid={`blog-card-${index}`}
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={post.image_url || "https://images.unsplash.com/photo-1604464543433-149fe779b779?w=600"}
                      alt={post.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-6">
                    <div className="text-sm text-slate-500 mb-2">
                      {new Date(post.created_at).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </div>
                    <h3 className="font-bold text-lg text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-slate-600 text-sm line-clamp-2">
                      {post.excerpt}
                    </p>
                    <span className="inline-flex items-center text-orange-600 font-semibold mt-4 gap-1 group-hover:gap-2 transition-all text-sm">
                      {t('blog.readMore')}
                      <ChevronRight size={16} aria-hidden="true" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            <div className="md:hidden text-center mt-8">
              <Link to="/blog">
                <Button variant="outline" className="border-slate-900 text-slate-900">
                  {t('blog.viewAll')}
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default HomePage;
